import { Suspense } from "react"
import { FallbackProps } from "react-error-boundary"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { QueryErrorFallback, QueryErrorBoundary, QueryErrorFallbackProps } from "../../../../../../core/api/components/QueryErrorBoundary"
import { DashboardCard } from "../../../../../dashboards/components/DashboardCard"
import { SmileyExtSummaryCard } from "../../../../admin/survey/components/SmileySummaryCard"
import { MerchantHomeQuery, fetchSurveyRatingSummaries } from "../../merchantHomeApi"
import { DashboardCardSkeleton } from "../../../../../dashboards/components/DashboardCardSkeleton"
import { useMerchant } from "../../../../../merchants/merchantHooks"

const useStyles = makeStyles()((_theme) => ({
    smileyImage: {
        height: "7rem",
    },
}))

type SurveySummariesProps = Readonly<{
    periodInDays: number
}>

function ContentLoader({ periodInDays }: SurveySummariesProps) {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()

    const { data } = useApiQuery(fetchSurveyRatingSummaries, {
        queryName: MerchantHomeQuery.SURVEY_RATING_SUMMARIES,
        dependencies: {
            merchantId,
            periodInDays,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    return (
        <>
            {data?.surveys.map((survey) => (
                <SmileyExtSummaryCard
                    title={survey.name ?? survey.title ?? ""}
                    surveyId={survey.id}
                    smileySummary={survey.ratingsSummary}
                    slotProps={{
                        image: { className: classes.smileyImage },
                    }}
                />
            ))}
        </>
    )
}

function ErrorFallback(props: QueryErrorFallbackProps) {
    return (
        <LocalizedStrict id="merchant-home-survey-card-empty" attrs={{ title: true }}>
            <DashboardCard title="Survey">
                <QueryErrorFallback {...props} />
            </DashboardCard>
        </LocalizedStrict>
    )
}

export function SurveySummaries({ periodInDays }: SurveySummariesProps) {
    return (
        <QueryErrorBoundary Fallback={ErrorFallback}>
            <Suspense fallback={<DashboardCardSkeleton />}>
                <ContentLoader periodInDays={periodInDays} />
            </Suspense>
        </QueryErrorBoundary>
    )
}
